import type { FileNameRulesForExternalSystems } from '../dto/tenantModel';
import type { DynamoItemType } from '../dto/itemTypes';
import type { ItemMetaData } from '../dto/fieldValue';
export type ObjType = 'STILL' | 'AUDIO' | 'VIDEO';
export type MosAckStatus = 'ACK' | 'ERROR' | 'NACK';

// http://mosprotocol.com/wp-content/MOS-Protocol-Documents/MOS_Protocol_Version_2.8.5_Final.htm#status
export type MOSStatusFieldType =  'NEW' | 'UPDATED' | 'MOVED' | 'BUSY ' | 'DELETED' | 'NCS CTRL' | 'MANUAL CTRL' | 'READY' | 'NOT READY' | 'PLAY' | 'STOP';


// Known MOS tags so far - helps with auto complete.
export type MosTag = 'ncsReqAppInfo' | 'mos' | 'ncsAck' | 'status' | 'statusDescription' | 'mosObj' | 'objType' | 'objDur' | 'objPaths' | 'ncsItem' | 'item' |
'objProxyPath' | 'objPath' | 'objID' | 'objSlug' | 'objTB' | 'mosAbstract' | 'description' | 'created' | 'createdBy' | 'changed' | 'changedBy' | 'mosID' | 'ncsID' | 'userID' |
'messageID' | 'heartbeat' | 'time' | 'listMachInfo' | 'manufacturer' | 'model' | 'hwRev' | 'swRev' | 'DOM' | 'SN' | 'ID' |
'mosRev' | 'supportedProfiles' | 'mosAck' | 'objAir' | 'mosExternalMetadata' | 'mosScope' | 'mosSchema' | 'mosPayload' | 'itemEdStart' |
'mosProfile0' | 'mosProfile1' | 'mosProfile2' | 'mosProfile3' | 'mosProfile4' | 'mosProfile5' | 'mosProfile6' | 'mosProfile7' | 'itemID' |
'itemSlug' | 'itemEdDur' | 'roAck' | 'roStatus' | 'roID' | 'storyID' | 'roReq' | 'mosItemReplace';

export type MimirMetadataTag = 'metadata' | 'field' | 'metaTime';

// Mimir's mosID.
export const DEFAULT_MOS_ID = 'Mimir';

export interface MosItemReplaceData {
  storyId: string,
  itemIdInStory: string,
}

export interface MosOpenItemInfo {
  currentMosOpenItemId: string | null,
  mosItemReplaceRequestId: string | null, // if a mosItemReplace message was posted and we are awaiting 'ACK' for that message, this is set to the requested id otherwise it stays null
}

export interface MosEventHandler {
  openItemId: (itemId: string, mosItemReplaceData?: MosItemReplaceData | null) => void;
  requestSelectedItem: () => MosObjCompatibleItem | null;
}

export interface MosContext {
  ncsID?: string;
  userID?: string;
  mosID: string;
  messageID?: number;
}

export interface LogMessageEvent {
  key: string;
  message: string;
  timestamp: string;
  responseKey?: string;
}


export interface MosObjOptions {
  status?: MOSStatusFieldType;

  // We use these fields when responding to a mosObjCreate message
  // to return to the NCS the exact values it gave us (which is why
  // we use strings and not numbers)
  objTBFromNcs?: string;
  objDurFromNcs?: string;

  // If set to true, we include a timestamp in the mosExternalMetadata.
  // This is needed to force a "refresh" in some situations when:
  //   - The ncs has already received a mosObj for an item
  //   - We add a ncsItem from the mos plugin for that item
  //   - We want the ncs to apply the state (such as objAir) from the mosObj to
  //     the ncsItem. Sending out a new mosObj with a payload different from a
  //     previously sent mosObj may then be needed.
  includeTimestamp?: boolean

  // This is a prefix that will be applied to image objPaths if supplied via the iopp URL parameter
  imageObjectPathPrefix?: string;

  // If automatic storage transfer is enabled in the MOS config and the storage has a target media format
  // configured, then this property will be used to specify the file extension of that format.
  objectPathSuffix?: string;

  // Whether to include the <objProxyPath/> elements containing thumbnail and proxy url in the MOS XML
  includeObjProxyPath?: boolean;

  objPathRules?: FileNameRulesForExternalSystems | null;
}

export const OBJ_PROXY_PATH_QUERY_PARAM = 'includeObjProxyPath';


/**
 * Defines the item data used for generating the mosObj xml.
 * Defining a separate type for this as we create mosObj
 * both from server entities and client search result items.
 */
export type MosObjCompatibleItem = {
  id: string,
  title: string,
  description: string | undefined,

  // The item metadata with the internal field id guids
  // replaced by the user defined fieldIds.
  exportFriendlyMetadata: ItemMetaData,
  mediaFrameRate?: number;
  mediaDuration?: number;
  thumbnail?: string;
  originalFileName?: string;
  itemType: DynamoItemType;
  mediaType?: string;
  proxy?: string | null;
  hasHighres: boolean;

  // Taken from metadata.fromData[SystemDefaultField.CreatedOn]
  created?: Date;

  // Taken from item.modifiedOn
  changed?: Date;
};
